/**
 * EPIC FLOW, English.
 *
 * Arabic is the source language; a missing key here falls back to it, so this
 * file is allowed to lag. It does not lag today, but the fallback is why that
 * would be acceptable rather than a bug.
 */
export const flowEn: Record<string, string> = {
  'flow.reception.greeting.morning': 'Good morning, {name}',
  'flow.reception.greeting.evening': 'Good evening, {name}',
  'flow.reception.subtitle': '{date} · {branch} · {doctors} doctors on duty',
  'flow.reception.registerPatient': 'Register patient',
  'flow.reception.bookAppointment': '+ Book appointment',
  'flow.reception.upcoming': 'Expected arrivals',
  'flow.reception.allAppointments': 'All appointments →',
  'flow.reception.clinicStatus': 'Clinic status',
  'flow.reception.search': 'Patient search',
  'flow.reception.searchPlaceholder': 'Name, phone, national ID or MRN',
  'flow.reception.searchHint': 'Enter at least two characters',
  'flow.reception.searchEmpty': 'No matching patients',
  'flow.reception.noUpcoming': 'No further arrivals expected today',
  'flow.reception.noDoctors': 'No doctors are assigned to this branch',

  'flow.kpi.today': "Today's appointments",
  'flow.kpi.waiting': 'Waiting',
  'flow.kpi.averageWait': 'Average wait',
  'flow.kpi.readyCheckout': 'Ready for checkout',
  'flow.kpi.completed': 'Completed',
  'flow.kpi.inProgress': 'In consultation',
  'flow.kpi.drafts': 'Unfinalised drafts',
  'flow.kpi.noShow': 'No-show',
  'flow.kpi.minutesSuffix': ' min',

  'flow.doctor.title': "Today's queue — {doctor}",
  'flow.doctor.subtitle': '{specialty} · {appointments} appointments · {drafts} open visit drafts',
  'flow.doctor.startNext': 'Start next consultation',
  'flow.doctor.openBlocking': 'Open the unfinished consultation',
  'flow.doctor.noDoctorRecord':
    'Your account is not linked to a doctor record, so there is no queue to show.',
  'flow.doctor.emptyQueue': 'Nobody is in your queue right now',
  'flow.doctor.tab.waiting': 'Waiting',
  'flow.doctor.tab.inProgress': 'In consultation',
  'flow.doctor.tab.readyCheckout': 'Ready for checkout',
  'flow.doctor.tab.completed': 'Completed',

  'flow.waiting.title': 'Waiting room',
  'flow.waiting.subtitle': '{branch} · polling every {seconds} seconds',
  'flow.waiting.tvMode': 'Display mode',
  'flow.waiting.recordArrival': 'Record arrival',
  'flow.waiting.empty': 'The waiting room is empty',
  'flow.column.arrived': 'Arrived',
  'flow.column.inProgress': 'In consultation',
  'flow.column.readyCheckout': 'Ready for checkout',
  'flow.column.completed': 'Completed',
  'flow.column.empty': 'Nobody',

  'flow.action.completeArrival': 'Complete check-in',
  'flow.action.arrive': 'Record arrival',
  'flow.action.start': 'Start consultation',
  'flow.action.readyCheckout': 'Ready for checkout',
  'flow.action.checkout': 'Checkout',
  'flow.action.startVisit': 'Open visit',
  'flow.action.openVisit': 'Continue visit',
  'flow.action.retry': 'Retry',
  'flow.action.cancel': 'Cancel',
  'flow.action.back': 'Back',
  'flow.action.next': 'Next',

  'flow.disabled.noPermission': 'You do not have permission for this action.',
  'flow.disabled.wrongState': "The appointment's current state does not allow this action.",
  'flow.disabled.doctorBusy': 'This doctor already has a consultation in progress.',
  'flow.disabled.notYours': 'This appointment belongs to another doctor.',

  'flow.wait.minutesShort': 'min',
  'flow.wait.notArrived': 'Not arrived',
  'flow.wait.threshold': 'Wait threshold {minutes} minutes',
  'flow.freshness.updating': 'Updating…',
  'flow.freshness.updated': 'Updated {seconds}s ago',
  'flow.subject.provisional': 'unidentified',

  'flow.arrival.title': 'Arrival',
  'flow.arrival.subtitle': 'Two steps: confirm identity, then fee policy and queue number.',
  'flow.arrival.step1': 'Identity and alerts',
  'flow.arrival.step2': 'Fee and confirmation',
  'flow.arrival.identityConfirmed': '✓ Identity confirmed',
  'flow.arrival.provisionalContact': 'Public booking — not yet an identified patient',
  'flow.arrival.identifyExisting': 'Link to an existing patient',
  'flow.arrival.createNew': 'Create a new patient record',
  'flow.arrival.fullName': 'Full name',
  'flow.arrival.phone': 'Phone',
  'flow.arrival.nationalId': 'National ID',
  'flow.arrival.duplicates': 'Possible duplicate patients',
  'flow.arrival.duplicatesBody':
    'Review these before creating a new record. Proceeding anyway needs a written reason.',
  'flow.arrival.overrideReason': 'Reason for overriding the duplicates',
  'flow.arrival.overrideReasonHint': 'Recorded in the audit trail with your name and the time.',
  'flow.arrival.alerts': 'Clinical alerts',
  'flow.arrival.alertsHidden': 'Clinical alerts are not available to your role.',
  'flow.arrival.noAlerts': 'No active clinical alerts.',
  'flow.arrival.balance': 'Outstanding balance',
  'flow.arrival.balanceHidden': 'Financial balance is not available to your role.',
  'flow.arrival.balanceInvoices': '{count} outstanding invoices',
  'flow.arrival.noBalance': 'No outstanding balance.',
  'flow.arrival.feePolicy': 'Fee policy',
  'flow.arrival.feeReason': 'Exemption reason',
  'flow.arrival.feeReasonHint': 'Required for a full waiver and recorded in the audit trail.',
  'flow.arrival.room': 'Room',
  'flow.arrival.confirm': 'Confirm arrival and issue queue number',
  'flow.arrival.suggestedQueue': 'Suggested queue number',
  'flow.arrival.queueNote':
    'The final number is issued on confirmation and may differ if a colleague registers an arrival first.',
  'flow.arrival.details': 'Appointment details',
  'flow.arrival.detail.time': 'Time',
  'flow.arrival.detail.service': 'Service',
  'flow.arrival.detail.source': 'Source',
  'flow.arrival.detail.doctor': 'Doctor',
  'flow.arrival.detail.branch': 'Branch',
  'flow.arrival.atomicNote':
    'Every arrival step (patient link, fee decision, queue number, status change) runs in one transaction — all of it commits or none of it does.',
  'flow.arrival.done': 'Arrival recorded. Queue number {number}.',
  'flow.arrival.needsRepair':
    'This patient was checked in without a queue number or a decided fee. Complete the check-in now.',
  'flow.arrival.alreadyArrived': 'This appointment has already been recorded as arrived.',
  'flow.arrival.needSubject':
    'Choose an existing patient or create a new record before continuing.',

  'flow.fee.STANDARD': 'First visit — full fee',
  'flow.fee.FOLLOW_UP': 'Follow-up fee',
  'flow.fee.SERVICE_PRICE': 'Approved service price',
  'flow.fee.EXEMPT': 'Full fee exemption',
  'flow.fee.FREE': 'Free',
  'flow.fee.unavailable': 'Not available for this appointment or your role',
  'flow.fee.requiresWaiver': 'Requires the “approve waivers” permission and a written reason',

  'flow.tv.current': 'Now serving',
  'flow.tv.next': 'Up next',
  'flow.tv.waitingHall': '{branch} — waiting hall',
  'flow.tv.privacyNote': 'Patient names and clinical data are never shown on this screen.',
  'flow.tv.exit': 'Exit display mode',
  'flow.tv.noCurrent': '—',
  'flow.tv.noNext': 'No one is waiting',

  'flow.conflict.stale': 'This appointment changed after you opened the screen. Reload and retry.',
  'flow.conflict.duplicates': 'Matching patients exist. Review them or give an override reason.',
};
